import {Injectable} from "@angular/core";
import {GridLayoutService} from "@irb/irb-common/interfaces/grid-layout.service";
import {DeleteHeaderComponent} from "@irb/irb-common/components/grid/renderers/delete.header.component";
import {DeleteCellComponent} from "@irb/irb-common/components/grid/renderers/delete.cell.component";
import {NumberTextBoxRendererComponent} from "@irb-common/components/grid/renderers/number-text-box.cell.component";
import {UtilityService} from "@irb/irb-common/services/utility.service";
import {PurchaseUnderlyingSettingsComponent} from "./purchase-underlying-settings.component";
import {OptionToSellCellRenderer} from "./option-to-sell-cell-renderer.compoent";
import {UnderlyingTargetCellRendererComponent} from "./underlying-target-cell-renderer/underlying-target-cell-renderer.component";
import {IDropdownItem} from "@irb/irb-common/models/dropdown.model";
import {DropdownFilterRendererComponent} from "@irb/irb-common/components/grid/renderers/dropdown-filter.cell.component";

@Injectable({
    providedIn: 'root'
})
export class PurchaseUnderlyingGridLayoutService implements GridLayoutService<PurchaseUnderlyingSettingsComponent> {

    sizeFor: any;

    constructor(private util: UtilityService) {
        this.sizeFor = {
            "underlyingSymbol": 180,
            "underlyingTarget": 180,
            "percentOfUnderlyingToPurchase": 200,
            "optionSymbol": 150,
            "percentOfUnderlyingToCover": 225
        };
    }

    getFrameworkComponents() {
        return {
            "DeleteHeaderComponent": DeleteHeaderComponent,
            "DeleteCellComponent": DeleteCellComponent,
            "OptionToSellCellRenderer": OptionToSellCellRenderer,
            "UnderlyingTargetCellRendererComponent": UnderlyingTargetCellRendererComponent,
            "DropdownFilterRendererComponent": DropdownFilterRendererComponent,
            "NumberTextBoxRendererComponent": NumberTextBoxRendererComponent
        };
    }

    percentOfUnderlyingToCoverValue(params: any) {
        let val = params.context.componentParent.decimalPipe.transform(params.data.percentOfUnderlyingToCover, '1.2-2');
        return val + " %";
    }

    percentOfUnderlyingToCoverValueSetter(params: any) {
        params.data.percentOfUnderlyingToCover = Number(params.newValue);
        return true;
    }

    percentOfUnderlyingToPurchaseValueGetter(params: any) {
        let percentOfUnderlyingToPurchase: any = "-";
        if (params.data.underlyingTarget) {
            switch(params.data.underlyingTarget) {
                case 'MODEL_PERC':
                    percentOfUnderlyingToPurchase = "-";
                    break;
                case 'SPECIFY_PERC':
                    percentOfUnderlyingToPurchase = params.context.componentParent.decimalPipe.transform(params.data.percentOfUnderlyingToPurchase, '1.2-2');
                    break;
            }
        }
        if(params.data.underlyingTarget === 'SPECIFY_PERC' && params.data.percentOfUnderlyingToPurchase) {
            return percentOfUnderlyingToPurchase + " %";
        } else {
            return percentOfUnderlyingToPurchase;
        }
    }

    percentOfUnderlyingToPurchaseValueSetter(params: any) {
        params.data.percentOfUnderlyingToPurchase = Number(params.newValue);
        return true;
    }

    isGridEditable(params: any) {
        return !params.context.componentParent.isReadOnly;
    }

    isUnderlyingTargetValueEditable(params: any) {
        return !params.context.componentParent.isReadOnly && (params.data.underlyingTarget === 'SPECIFY_PERC');
    }

    getColumnDefs(parent: PurchaseUnderlyingSettingsComponent) {
        return [
            {
                headerName: 'Delete All',
                headerComponent: 'DeleteHeaderComponent',
                headerComponentParams: {
                    message: "Remove all items?",
                    proceed: parent.deleteAll.bind(parent),
                    cancel: parent.cancelDelete.bind(parent),
                    disableDelete: parent.isReadOnly
                },
                cellRenderer: 'DeleteCellComponent',
                cellRendererParams: {
                    message: "Remove this item?",
                    proceed: parent.delete.bind(parent),
                    cancel: parent.cancelDelete.bind(parent),
                    disableDelete: this.isGridEditable.bind(this)
                },
                resizable: false,
                minWidth: 25,
                maxWidth: 25,
                width: 25,
            },
            {
                headerName: 'Underlying Symbol',
                field: 'underlyingSymbol',
                minWidth: this.sizeFor.underlyingSymbol,
                flex: 1,
                sort: "asc",
                resizable: false,
                comparator: this.util.sortString.bind(parent),
                editable: false,
                suppressMovable: true,
                cellHeight: 30,
            },
            {
                headerName: 'Underlying target',
                headerComponentParams: {
                    irbColInfo: `<p>If Model Target is selected, iRebal will attempt to purchase the underlying equity to the Model Target. If Specify % is selected, you'll need to enter a value for Underlying % of the Portfolio, and iRebal will attempt to purchase the underlying equity to your specified percentage.</p>`
                },
                field: 'underlyingTarget',
                minWidth: this.sizeFor.underlyingTarget,
                flex: 1,
                cellClass: "dropdownFilterCell",
                // cellRenderer: 'UnderlyingTargetCellRendererComponent',
                cellRenderer: "DropdownFilterRendererComponent",
                cellRendererParams: (params: any) => {
                  return {
                    defaultValue: parent.getDefaultValueForppPercentOfUnderlying(params.data.underlyingTarget),
                    values: parent.percentOfUnderlyingDropdown,
                    onChange: (params: any, item: IDropdownItem) => {
                      params.data.underlyingTarget = item.value;
    
                      if (params.data.underlyingTarget === "MODEL_PERC") {
                        params.data.percentOfUnderlyingToPurchase = null;
                      } else {
                        params.data.percentOfUnderlyingToPurchase = params.data.percentOfUnderlyingToPurchase || 0;
                        setTimeout(() => {
                          const elem: any = document.querySelectorAll(".ag-row-focus .capBuyClassForFocus input");
    
                          if (elem && elem.length !== 0) {
                            elem[0].focus();
                          }
                        }, 100);
                      }
    
                      params.api.redrawRows({ rowNodes: [params.node] });
                    },
                    onClearSelection: (params: any) => {
                      params.data.underlyingTarget = "MODEL_PERC";
                      params.data.percentOfUnderlyingToPurchase = null;
                      params.api.redrawRows({ rowNodes: [params.node] });
                    }
                  };
                },
                sortable: false,
                resizable: false,
                suppressMovable: true,
                cellHeight: 30,
            },
            {
                headerName: 'Underlying % of Portfolio',
                headerComponentParams: {
                    irbColInfo: `<p>This is the overall percentage the underlying equity will be purchased to. It will be based on the percentage of the portfolio. Example: entering 4% tells iRebal to purchase the security to 4% of the portfolio. If the security is greater than 4% no action will be taken.</p>`
                },
                field: 'percentOfUnderlyingToPurchase',
                // valueGetter: this.percentOfUnderlyingToPurchaseValueGetter,
                // valueSetter: this.percentOfUnderlyingToPurchaseValueSetter,
                // cellEditorFramework: UnderlyingToPurchaseCellEditor,
                minWidth: this.sizeFor.percentOfUnderlyingToPurchase,
                flex: 1,
                // editable: this.isUnderlyingTargetValueEditable,
                cellClass: "numberTextBoxCell",
                cellRenderer: "NumberTextBoxRendererComponent",
                cellRendererParams: (params: any) => {
                    return {
                        disabled: !parent.ispercentOfUnderlyingToPurchaseEditable(params),
                        onChange: (params: any, c: any) => {
                            if (params && params.data.percentOfUnderlyingToPurchase != c.value && c.isValid) {
                                params.data.percentOfUnderlyingToPurchase = parseFloat(c.value);
                                params.api.redrawRows({ rowNodes: [params.node] });
                            } 
                        },
                    };
                },
                resizable: false,
                suppressMovable: true,
                sortable: false
            },
            {
                headerName: 'Option to sell',
                headerComponentParams: {
                    irbColInfo: `<p>This is the option iRebal will sell against your underlying equity position.</p>`
                },
                field: 'optionSymbol',
                width: this.sizeFor.optionSymbol,
                minWidth: this.sizeFor.optionSymbol,
                flex: 1,
                sort: "asc",
                resizable: false,
                comparator: this.util.sortString.bind(parent),
                editable: false,
                cellRenderer: 'OptionToSellCellRenderer',
                suppressMovable: true,
                cellHeight: 30,
            },
            {
                headerName: 'Percent of underlying to cover',
                headerComponentParams: {
                    irbColInfo: `<p>Enter a % of the current underlying security to sell a covered call against. Examples: entering 100% tells iRebal to sell a covered call against the full share quantity that isn't already covered in the account; entering 50% will indicate to sell a covered call against 50% of the share quantity in the account which doesn't have a covered call written against it.</p>`
                },
                field: 'percentOfUnderlyingToCover',
                // valueGetter: this.percentOfUnderlyingToCoverValue,
                // valueSetter: this.percentOfUnderlyingToCoverValueSetter,
                // cellEditorFramework: UnderlyingToCoverCellEditor,
                minWidth: this.sizeFor.percentOfUnderlyingToCover,
                flex: 1,
                // editable: this.isGridEditable,
                cellClass: "numberTextBoxCell",
                cellRenderer: "NumberTextBoxRendererComponent",
                cellRendererParams: () => {
                    return {
                        min: 0,
                        max: 100,
                        places: 2,
                        autofocus: true,
                        isPercentage: true,
                        onChange: (params: any, c: any) => {
                            if (params && params.data.percentOfUnderlyingToCover != c.value && c.isValid) {
                                params.data.percentOfUnderlyingToCover = parseFloat(c.value);
                                params.api.redrawRows({ rowNodes: [params.node] });
                            }
                        },
                    };
                },
                resizable: false,
                suppressMovable: true,
                sortable: false
            }
        ];
    }

}
